import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { isAuthenticated } = useAuth();
  const { cart, clearCart } = useCart();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const quoteData = {
        ...formData,
        items: cart.items || [],
      };

      await axios.post(`${API_URL}/quote`, quoteData);
      toast.success('Orçamento enviado com sucesso! Entraremos em contato em breve.');
      
      if (isAuthenticated && cart.items?.length > 0) {
        await clearCart();
      }
      
      setFormData({ name: '', email: '', phone: '', message: '' });
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      toast.error('Erro ao enviar orçamento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Formatar telefone para exibição
  const formatPhone = (phone) => {
    if (!phone) return '';
    // Remove tudo que não é número
    const numbers = phone.replace(/\D/g, '');
    // Formato: (XX) XXXXX-XXXX
    if (numbers.length === 11) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    }
    if (numbers.length === 13) { // Com código do país
      return `(${numbers.slice(2, 4)}) ${numbers.slice(4, 9)}-${numbers.slice(9)}`;
    }
    return phone;
  };

  return (
    <div data-testid="contact-page" className="py-20 md:py-32">
      <div className="px-6 md:px-12 lg:px-24">
        <div className="text-center mb-16">
          <h1 data-testid="contact-title" className="font-heading text-5xl md:text-6xl font-light tracking-tight mb-4">
            Entre em Contato
          </h1>
          <p className="text-base font-light leading-relaxed text-muted-foreground">
            Solicite um orçamento personalizado para seus móveis
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <div>
            <h2 className="font-accent text-xs uppercase tracking-widest mb-8">Solicitar Orçamento</h2>
            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-8">
              <div>
                <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Nome
                </label>
                <input
                  type="text"
                  data-testid="name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-4 focus:ring-0 outline-none transition-colors duration-300 placeholder:text-muted-foreground/50"
                  placeholder="Seu nome completo"
                />
              </div>

              <div>
                <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  E-mail
                </label>
                <input
                  type="email"
                  data-testid="email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-4 focus:ring-0 outline-none transition-colors duration-300 placeholder:text-muted-foreground/50"
                  placeholder="seu@email.com"
                />
              </div>

              <div>
                <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Telefone
                </label>
                <input
                  type="tel"
                  data-testid="phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-4 focus:ring-0 outline-none transition-colors duration-300 placeholder:text-muted-foreground/50"
                  placeholder="(88) 99999-9999"
                />
              </div>

              <div>
                <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Mensagem
                </label>
                <textarea
                  data-testid="message-input"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={6}
                  className="w-full bg-transparent border border-border focus:border-primary p-4 focus:ring-0 outline-none transition-colors duration-300 placeholder:text-muted-foreground/50 resize-none"
                  placeholder="Conte-nos sobre seu projeto..."
                />
              </div>

              {cart.items?.length > 0 && (
                <div className="p-6 bg-secondary">
                  <p className="text-sm text-muted-foreground mb-2">
                    Produtos no carrinho serão incluídos no orçamento:
                  </p>
                  <p className="text-sm font-medium">{cart.items.length} item(s)</p>
                </div>
              )}

              <button
                type="submit"
                data-testid="submit-button"
                disabled={loading}
                className="w-full bg-primary text-white px-8 py-4 uppercase tracking-widest text-xs hover:bg-black transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Enviando...' : 'Enviar Orçamento'}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div>
            <h2 className="font-accent text-xs uppercase tracking-widest mb-8">Informações de Contato</h2>
            <div className="space-y-8">
              <div className="flex gap-4">
                <Mail className="w-5 h-5 flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <p className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2">
                    E-mail
                  </p>
                  <a 
                    href={`mailto:${settings?.contact_email || 'contato@casaingold.com.br'}`}
                    className="text-base font-light hover:text-primary transition-colors"
                  >
                    {settings?.contact_email || 'contato@casaingold.com.br'}
                  </a>
                </div>
              </div>

              <div className="flex gap-4">
                <Phone className="w-5 h-5 flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <p className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2">
                    Telefone / WhatsApp
                  </p>
                  <a 
                    href={`https://wa.me/${settings?.contact_whatsapp || '5588996762222'}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-base font-light hover:text-primary transition-colors"
                  >
                    {formatPhone(settings?.contact_phone) || formatPhone(settings?.contact_whatsapp) || '(88) 99676-2222'}
                  </a>
                </div>
              </div>

              <div className="flex gap-4">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <p className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2">
                    Endereço
                  </p>
                  <p className="text-base font-light leading-relaxed whitespace-pre-line">
                    {settings?.contact_address || 'R. Dr. Ronaldo Ponte Dias, 1386\nSobral - CE\nCEP 62043-410'}
                  </p>
                </div>
              </div>

              <div className="pt-8">
                <h3 className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-4">
                  Horário de Atendimento
                </h3>
                <p className="text-base font-light leading-relaxed text-muted-foreground">
                  {settings?.hours_weekday || 'Segunda a Sexta: 9h às 18h'}<br />
                  {settings?.hours_weekend || 'Sábado: 10h às 14h'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
